import { ref } from "vue";
import type { Ref } from "vue";
import { KeyCode } from "@/keyboard";
import { useEventListener } from "@/composables";

export const useCaptureKeys = (
  target: Ref<EventTarget | null> | EventTarget
) => {
  const keycodes = ref<Set<KeyCode>>(new Set());

  const checkTarget = (target: EventTarget) => {
    const elem = target as HTMLElement;
    // Check if element is focusable (not extensive, see: https://stackoverflow.com/a/18261861)
    if (
      elem.getAttribute("tabindex") == null &&
      elem.nodeName.toLowerCase() != "a" &&
      elem.nodeName.toLowerCase() != "area"
    )
      throw Error("Keyboard input capture requires focusable element.");
  };

  const onKeyDown = (e: KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Ignore keys being held
    if (e.repeat) return;

    const kc = keyToKeyberon(e);

    if (kc != undefined) keycodes.value.add(kc);
    else console.warn("Unknown key:", e.key);
  };

  // Reset keys input when we get focus
  const onSelfFocusIn = (_: FocusEvent) => {
    keycodes.value.clear();
  };

  useEventListener(
    target,
    "keydown",
    onKeyDown as (e: Event) => void,
    checkTarget
  );
  useEventListener(target, "focus", onSelfFocusIn as (e: Event) => void);

  return keycodes;
};

const keyToKeyberon = (e: KeyboardEvent): KeyCode | undefined => {
  // Handle "special" locations
  if (e.location == e.DOM_KEY_LOCATION_LEFT) {
    if (e.key == "Shift") return "LShift";
    if (e.key == "Control") return "LCtrl";
    if (["Hyper", "Meta", "OS", "Super"].includes(e.key)) return "LGui";
  } else if (e.location == e.DOM_KEY_LOCATION_RIGHT) {
    if (e.key == "Shift") return "RShift";
    if (e.key == "Control") return "RCtrl";
    if (["Hyper", "Meta", "OS", "Super"].includes(e.key)) return "RGui";
  } else if (e.location == e.DOM_KEY_LOCATION_NUMPAD) {
    if (e.key == "0") return "Kp0";
    if (e.key == "1") return "Kp1";
    if (e.key == "2") return "Kp2";
    if (e.key == "3") return "Kp3";
    if (e.key == "4") return "Kp4";
    if (e.key == "5") return "Kp5";
    if (e.key == "6") return "Kp6";
    if (e.key == "7") return "Kp7";
    if (e.key == "8") return "Kp8";
    if (e.key == "9") return "Kp9";
    if (e.key == "/") return "KpSlash";
    if (e.key == "*") return "KpAsterisk";
    if (e.key == "-") return "KpMinus";
    if (e.key == "+") return "KpPlus";
    if (e.key == "=") return "KpEqual";
    if (e.key == "Enter") return "KpEnter";
    if (e.key == ",") return "KpDot";
    if (e.key == ".") return "KpDot";
  }
  // Handle a-z/A-Z
  if ("abcdefghijklmnopqrstuvwxyz".split("").includes(e.key.toLowerCase()))
    return e.key.toUpperCase() as KeyCode;
  return KEY_TO_KEYBERON[e.key];
};

// Mapping from KeyboardEvent.key to keyberon.KeyCode
const KEY_TO_KEYBERON: Record<string, KeyCode | undefined> = {
  // Modifier keys
  Alt: "LAlt",
  AltGraph: "RAlt",
  CapsLock: "CapsLock",
  Control: "LCtrl",
  Fn: undefined, // keyboards usually generate concrete keys like F1, F12, etc.
  FnLock: undefined,
  Hyper: "LGui", // just map Hyper/Meta/Super to Gui
  Meta: "LGui",
  OS: "LGui",
  NumLock: "NumLock",
  ScrollLock: "ScrollLock",
  Shift: "LShift",
  Super: "LGui",
  Symbol: undefined,
  SymbolLock: undefined,
  // Whitespace
  Enter: "Enter",
  Tab: "Tab",
  " ": "Space",
  Spacebar: "Space",
  // Navigation keys
  ArrowDown: "Down",
  ArrowLeft: "Left",
  ArrowRight: "Right",
  ArrowUp: "Up",
  Down: "Down",
  Left: "Left",
  Right: "Right",
  Up: "Up",
  End: "End",
  Home: "Home",
  PageDown: "PgDown",
  PageUp: "PgUp",
  // Editing keys
  Backspace: "BSpace",
  Clear: "Clear",
  Copy: "Copy",
  CrSel: "CrSel",
  Cut: "Cut",
  Delete: "Delete",
  Del: "Delete",
  EraseEof: undefined,
  ExSel: "ExSel",
  Insert: "Insert",
  Paste: "Paste",
  Redo: undefined,
  Undo: "Undo",
  // UI keys
  Accept: undefined,
  Again: "Again",
  Attn: undefined,
  Cancel: "Cancel",
  ContextMenu: "Menu",
  Escape: "Escape",
  Execute: "Execute",
  Find: "Find",
  Finish: undefined,
  Help: "Help",
  Pause: "Pause",
  Play: undefined,
  Props: undefined,
  Select: "Select",
  ZoomIn: undefined,
  ZoomOut: undefined,
  // Device keys
  BrightnessDown: undefined,
  BrightnessUp: undefined,
  Eject: "MediaEjectCD",
  LogOff: undefined,
  Power: undefined,
  PowerOff: undefined,
  PrintScreen: "PScreen",
  Hibernate: undefined,
  Standby: undefined,
  WakeUp: undefined,
  // Common IME keys
  // Korean keyboards only
  // Japanese keyboards only
  // Function keys
  F1: "F1",
  F2: "F2",
  F3: "F3",
  F4: "F4",
  F5: "F5",
  F6: "F6",
  F7: "F7",
  F8: "F8",
  F9: "F9",
  F10: "F10",
  F11: "F11",
  F12: "F12",
  F13: "F13",
  F14: "F14",
  F15: "F15",
  F16: "F16",
  F17: "F17",
  F18: "F18",
  F19: "F19",
  F20: "F20",
  Soft1: "F21",
  Soft2: "F22",
  Soft3: "F23",
  Soft4: "F24",
  // Phone keys
  // Multimedia keys
  ChannelDown: undefined,
  ChannelUp: undefined,
  MediaFastForward: "MediaForward",
  MediaPause: "MediaPlayPause",
  MediaPlay: "MediaPlayPause",
  MediaPlayPause: "MediaPlayPause",
  MediaRecord: undefined,
  MediaStop: "MediaStop",
  MediaTrackNext: "MediaNextSong",
  MediaTrackPrevious: "MediaPreviousSong",
  // Audio control keys
  AudioBalanceLeft: undefined,
  AudioBalanceRight: undefined,
  AudioBassDown: undefined,
  AudioBassBoostDown: undefined,
  AudioBassBoostToggle: undefined,
  AudioBassBoostUp: undefined,
  AudioBassUp: undefined,
  AudioFaderFront: undefined,
  AudioFaderRear: undefined,
  AudioSurroundModeNext: undefined,
  AudioTrebleDown: undefined,
  AudioTrebleUp: undefined,
  AudioVolumeDown: "MediaVolDown",
  AudioVolumeMute: "MediaMute",
  AudioVolumeUp: "MediaVolUp",
  MicrophoneToggle: undefined,
  MicrophoneVolumeDown: undefined,
  MicrophoneVolumeMute: undefined,
  MicrophoneVolumeUp: undefined,
  // TV control keys
  // Media controller keys
  // Speech recognition keys
  // Document keys
  // Application selector keys
  LaunchCalculator: "MediaCalc",
  LaunchWebBrowser: "MediaWWW",
  // Browser control keys
  // Numeric keypad keys
  Decimal: "KpDot",
  Multiply: "KpAsterisk",
  Add: "KpPlus",
  Divide: "KpSlash",
  Subtract: "KpMinus",
  Separator: "KpDot",
  // Digits
  "1": "Kb1",
  "2": "Kb2",
  "3": "Kb3",
  "4": "Kb4",
  "5": "Kb5",
  "6": "Kb6",
  "7": "Kb7",
  "8": "Kb8",
  "9": "Kb9",
  "0": "Kb0",
  "!": "Kb1",
  "@": "Kb2",
  "#": "Kb3",
  $: "Kb4",
  "%": "Kb5",
  "^": "Kb6",
  "&": "Kb7",
  "*": "Kb8",
  "(": "Kb9",
  ")": "Kb0",
  // Special
  "-": "Minus",
  _: "Minus",
  "=": "Equal",
  "+": "Equal",
  "[": "LBracket",
  "{": "LBracket",
  "]": "RBracket",
  "}": "RBracket",
  "\\": "Bslash",
  "|": "Bslash",
  ";": "SColon",
  ":": "SColon",
  "'": "Quote",
  '"': "Quote",
  "`": "Grave",
  "~": "Grave",
  ",": "Comma",
  "<": "Comma",
  ".": "Dot",
  ">": "Dot",
  "/": "Slash",
  "?": "Slash",
} as const;
